import { Lock } from 'lucide-react';

const STATUS_LABELS = {
  agendada: 'Não Confirmada',
  confirmada: 'Confirmada',
  realizada: 'Realizada',
  cancelada: 'Cancelada',
  falta: 'Falta',
};

const STATUS_DOT = {
  agendada: '#D97706',
  confirmada: '#059669',
  realizada: '#6B7280',
  cancelada: '#DC2626',
  falta: '#EA580C',
};

const formatBRL = (v) =>
  `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const TYPE_OF = (c) => {
  if (c.categoria === 'Horário Bloqueado' || c.paciente_nome === 'Horário Bloqueado') return 'blocked';
  if (c.tipo_consulta === 'retorno' || c.tipo_cobranca === 'retorno_gratuito') return 'retorno';
  if (c.modo_atendimento === 'online') return 'online';
  return 'presencial';
};

const computeFin = (c, totalPago) => {
  const isBlocked = TYPE_OF(c) === 'blocked';
  const isCancelledOrMissed = c.status === 'cancelada' || c.status === 'falta';
  const isRetorno = TYPE_OF(c) === 'retorno';
  const valorTotal = c.valor_total || 0;
  const pago = totalPago !== undefined ? totalPago : (c.total_pago || 0);
  // Retornos não exibem flag financeira: o pagamento vive na consulta original
  const showFin = !isBlocked && !isCancelledOrMissed && !isRetorno;
  let tone = 'gray';
  if (pago === 0) tone = 'red';
  else if (valorTotal > 0 && pago >= valorTotal) tone = 'green';
  else tone = 'amber';
  return { showFin, tone, pago, valorTotal };
};

const AURAS = {
  presencial: 'bg-blue-100/90 ring-blue-200/90',
  online: 'bg-indigo-100/90 ring-indigo-200/90',
  retorno: 'bg-purple-100/90 ring-purple-200/90',
  blocked: 'bg-stone-200 ring-stone-300',
};

const LABEL_TONES = {
  presencial: 'text-blue-700',
  online: 'text-indigo-700',
  retorno: 'text-purple-700',
  blocked: 'text-stone-500',
};

const FIN_BG = {
  red: 'bg-red-100/80 text-red-700',
  amber: 'bg-amber-100/80 text-amber-800',
  green: 'bg-emerald-100/80 text-emerald-700',
  gray: 'bg-gray-100 text-gray-400',
};

/**
 * Card de consulta com design "Boutique / Pastel Capsule".
 * @param consulta — objeto consulta com paciente_nome, hora_inicio, hora_fim, categoria, status, modo_atendimento, tipo_consulta, preventivo, valor_total, total_pago
 * @param compact — boolean: versão reduzida para grade da agenda
 * @param miniCompact — boolean: versão MUITO reduzida (apenas hora + nome) para colunas estreitas da agenda semanal
 * @param totalPago — opcional, sobrescreve total_pago da consulta (usado pela agenda quando o resumo vem de outra collection)
 * @param hideFinBadge — boolean: oculta o badge financeiro flutuante (ex.: dashboards onde o pagamento não foi carregado)
 * @param onClick — handler de clique
 */
export default function EventCard({ consulta, compact = false, miniCompact = false, onClick, totalPago, hideFinBadge = false }) {
  const t = TYPE_OF(consulta);
  const isBlocked = t === 'blocked';
  const fin = computeFin(consulta, totalPago);
  const statusDot = STATUS_DOT[consulta.status] || '#9CA3AF';

  const aura = AURAS[t];
  const labelTone = LABEL_TONES[t];
  const finBg = FIN_BG[fin.tone];

  const typeLabel = t === 'retorno' ? 'Retorno' : t === 'online' ? 'Online' : 'Presencial';

  if (isBlocked) {
    if (miniCompact) {
      return (
        <div
          onClick={onClick}
          className={`${aura} ring-1 rounded-lg px-1.5 py-1 ${onClick ? 'cursor-pointer hover:ring-2' : ''} transition-all`}
          data-testid={`evento-bloqueado-${consulta.id}`}
        >
          <div className="text-[9px] font-semibold text-stone-500 tabular-nums tracking-wider leading-tight">
            {consulta.hora_inicio}
          </div>
          <div className="text-[11px] font-semibold text-stone-700 leading-tight mt-0.5 flex items-center gap-1">
            <Lock className="w-2.5 h-2.5 flex-shrink-0" /> Bloqueado
          </div>
        </div>
      );
    }
    return (
      <div
        onClick={onClick}
        className={`${aura} ring-1 rounded-2xl ${compact ? 'p-2' : 'p-3.5'} ${onClick ? 'cursor-pointer hover:ring-2' : ''} transition-all`}
        data-testid={`evento-bloqueado-${consulta.id}`}
      >
        <div className="flex items-center gap-1.5 text-stone-500">
          <Lock className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
          <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-semibold tracking-tight`}>
            Bloqueado
          </span>
        </div>
        <div className={`${compact ? 'text-[9px]' : 'text-[10px]'} text-stone-400 mt-0.5 tabular-nums tracking-wide`}>
          {consulta.hora_inicio} → {consulta.hora_fim}
        </div>
      </div>
    );
  }

  // Versão MUITO compacta — hora em cima, nome em baixo (para colunas estreitas)
  if (miniCompact) {
    return (
      <div
        onClick={onClick}
        className={`${aura} ring-1 rounded-lg px-1.5 py-1 ${onClick ? 'cursor-pointer hover:ring-2' : ''} transition-all`}
        data-testid={`evento-${consulta.id}`}
        title={`${consulta.hora_inicio} ${consulta.paciente_nome} — ${consulta.categoria}`}
      >
        <div className="text-[9px] font-semibold text-gray-500 tabular-nums tracking-wider leading-tight">
          {consulta.hora_inicio}
        </div>
        <div className="text-[11px] font-semibold text-gray-900 leading-tight mt-0.5 break-words">
          {consulta.paciente_nome}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`${aura} ring-1 rounded-2xl ${compact ? 'p-2.5' : 'p-3.5'} ${onClick ? 'cursor-pointer hover:ring-2' : ''} transition-all relative`}
      data-testid={`evento-${consulta.id}`}
    >
      {/* Top: time pill + status */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full bg-white/80 backdrop-blur-sm ${compact ? 'text-[9px]' : 'text-[10px]'} font-bold text-gray-700 tabular-nums tracking-wider shadow-sm flex-shrink-0`}
        >
          {consulta.hora_inicio}
          {!compact && consulta.hora_fim && <span className="text-gray-400 ml-1">– {consulta.hora_fim}</span>}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusDot }}></span>
          <span className={`${compact ? 'text-[8px]' : 'text-[9px]'} uppercase tracking-wider font-semibold text-gray-600 whitespace-nowrap`}>
            {STATUS_LABELS[consulta.status] || ''}
          </span>
        </div>
      </div>

      {/* Patient + categoria */}
      <div className={`${compact ? 'text-[12px]' : 'text-[13px]'} font-semibold text-gray-900 truncate leading-tight`}>
        {consulta.paciente_nome}
      </div>
      <div className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-gray-700 font-medium truncate mt-0.5`}>
        {consulta.categoria}
      </div>

      {/* Tags as pills */}
      {!compact && (
        <div className="flex flex-wrap gap-1 mt-2">
          <span className={`inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-bold ${labelTone} bg-white/60 backdrop-blur-sm`}>
            {typeLabel}
          </span>
          {consulta.preventivo === 'sim' && (
            <span className="inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-bold bg-white/60 text-amber-700 backdrop-blur-sm">
              Preventivo
            </span>
          )}
        </div>
      )}

      {compact && (
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          <span className={`inline-flex items-center px-1.5 py-px rounded-full text-[8px] font-bold ${labelTone} bg-white/70 backdrop-blur-sm`}>
            {typeLabel}
          </span>
          {consulta.tipo_consulta === 'retorno' && t !== 'retorno' && (
            <span className="inline-flex items-center px-1.5 py-px rounded-full text-[8px] font-bold text-purple-700 bg-white/70 backdrop-blur-sm">
              Retorno
            </span>
          )}
          {consulta.preventivo === 'sim' && (
            <span className="inline-flex items-center px-1.5 py-px rounded-full text-[8px] font-bold text-amber-700 bg-white/70 backdrop-blur-sm">
              Preventivo
            </span>
          )}
        </div>
      )}

      {/* Fin badge floating */}
      {!hideFinBadge && fin.showFin && (
        <span
          className={`absolute -top-1.5 -right-1.5 px-2 py-0.5 rounded-full ${compact ? 'text-[9px]' : 'text-[10px]'} font-bold tabular-nums shadow-sm ${finBg} backdrop-blur-sm`}
        >
          {formatBRL(fin.pago)}
        </span>
      )}
    </div>
  );
}
